import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError, API, getAccessToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, MessageSquare, MoreVertical, Plus, Send, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useTheme } from "@/components/ThemeProvider";
import { useAuth } from "@/lib/auth";
import { Link } from "react-router-dom";

const STARTERS = [
  "How many hours did I log this week?",
  "What's my overtime policy?",
  "Which entries are missing tags?",
];

function relative(ts) {
  if (!ts) return "";
  const then = new Date(ts).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const m = Math.floor(diff / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(ts).toLocaleDateString();
}

export default function PacePage() {
  const { theme } = useTheme();
  const brand = theme?.product_display_name || "Quanta";
  const [conversations, setConversations] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [thread, setThread] = useState(null);      // {conversation, messages}
  const [loadingThread, setLoadingThread] = useState(false);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState("");
  const [streamingText, setStreamingText] = useState(""); // Phase 18.1 — SSE delta accumulator
  const [renameFor, setRenameFor] = useState(null); // convo
  const [renameText, setRenameText] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [status, setStatus] = useState(null); // {pace_enabled}

  const [searchParams, setSearchParams] = useSearchParams();
  const deepLink = searchParams.get("c");
  const composerRef = useRef(null);
  const scrollerRef = useRef(null);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/pace/status");
      setStatus(data);
    } catch {
      setStatus({ pace_enabled: false });
    }
  }, []);

  const loadConversations = useCallback(async () => {
    setLoadingList(true);
    try {
      const { data } = await api.get("/pace/conversations");
      setConversations(data.items || []);
    } catch {
      setConversations([]);
    } finally {
      setLoadingList(false);
    }
  }, []);

  const loadThread = useCallback(async (id) => {
    if (!id) return;
    setLoadingThread(true);
    try {
      const { data } = await api.get(`/pace/conversations/${id}`);
      setThread(data);
    } catch (e) {
      if (e?.response?.status === 404) {
        toast.error("Conversation not found");
        setActiveId(null);
        setThread(null);
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setLoadingThread(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
    loadConversations();
  }, [loadStatus, loadConversations]);

  // Deep link resolution
  useEffect(() => {
    if (deepLink && deepLink !== activeId) {
      setActiveId(deepLink);
    }
  }, [deepLink, activeId]);

  // Load thread whenever active id changes
  useEffect(() => {
    if (activeId) {
      loadThread(activeId);
    } else {
      setThread(null);
    }
  }, [activeId, loadThread]);

  // Auto-scroll to bottom on thread update
  useEffect(() => {
    if (!scrollerRef.current) return;
    scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [thread, sending]);

  const openConversation = (id) => {
    setActiveId(id);
    setSearchParams({ c: id });
  };
  const newConversation = () => {
    setActiveId(null);
    setThread(null);
    setSearchParams({});
    setTimeout(() => composerRef.current?.focus(), 60);
  };

  const send = async (text) => {
    const q = (text ?? draft).trim();
    if (!q || sending) return;
    setSending(true);
    setStreamingText("");
    // Optimistic: append the user turn immediately so the UI feels alive.
    setThread((prev) => {
      const nextMessages = [
        ...((prev && prev.messages) || []),
        {
          id: `local-user-${Date.now()}`,
          role: "user",
          content: q,
          created_at: new Date().toISOString(),
        },
      ];
      return prev
        ? { ...prev, messages: nextMessages }
        : { conversation: { id: null, title: "" }, messages: nextMessages };
    });
    setDraft("");
    let newConvId = activeId;
    let finalRenderedBy = "llm";
    let hadDelta = false;
    try {
      const res = await fetch(`${API}/pace/ask/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAccessToken()}`,
        },
        body: JSON.stringify({ question: q, conversation_id: activeId || undefined }),
      });
      if (!res.ok || !res.body) {
        let msg = `Request failed (${res.status})`;
        try {
          const body = await res.json();
          if (body?.detail) msg = typeof body.detail === "string" ? body.detail : JSON.stringify(body.detail);
        } catch (_) {
          /* stream not JSON */
        }
        if (res.status === 429) toast.warning(msg);
        else toast.error(msg);
        setSending(false);
        setStreamingText("");
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";
      let accumulated = "";
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // Split on the SSE frame boundary \n\n.
        let idx;
        while ((idx = buffer.indexOf("\n\n")) !== -1) {
          const frame = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          const evLine = frame.match(/^event: (.+)$/m);
          const dataLine = frame.match(/^data: (.+)$/m);
          if (!evLine || !dataLine) continue;
          const event = evLine[1].trim();
          let data;
          try { data = JSON.parse(dataLine[1]); } catch (_) { continue; }
          if (event === "session") {
            newConvId = data.conversation_id;
            if (!activeId) {
              setActiveId(newConvId);
              setSearchParams({ c: newConvId });
            }
          } else if (event === "delta" && data?.text) {
            hadDelta = true;
            accumulated += data.text;
            setStreamingText(accumulated);
          } else if (event === "error") {
            // Backend will follow with delta+done for a graceful degrade;
            // don't toast — the fallback answer will render inline.
            finalRenderedBy = "fallback";
          } else if (event === "done") {
            if (data?.rendered_by) finalRenderedBy = data.rendered_by;
          }
        }
      }
    } catch (e) {
      toast.error(e?.message || "Streaming failed");
    } finally {
      setSending(false);
      setStreamingText("");
      // Reload the thread from the server as the source of truth (assistant
      // message id, rendered_by, canonical order, etc.).
      if (newConvId) {
        setActiveId(newConvId);
        setSearchParams({ c: newConvId });
        await loadThread(newConvId);
        await loadConversations();
      }
      // Silence unused-vars for finalRenderedBy/hadDelta — kept for
      // future analytics hooks.
      void finalRenderedBy; void hadDelta;
    }
  };

  const rename = async () => {
    if (!renameFor) return;
    const title = renameText.trim();
    if (!title) return;
    try {
      await api.patch(`/pace/conversations/${renameFor.id}`, { title });
      setRenameFor(null);
      await loadConversations();
      if (activeId === renameFor.id) await loadThread(activeId);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/pace/conversations/${confirmDelete.id}`);
      const wasActive = confirmDelete.id === activeId;
      setConfirmDelete(null);
      await loadConversations();
      if (wasActive) {
        setActiveId(null);
        setThread(null);
        setSearchParams({});
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onComposerKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  if (status && !status.pace_enabled) {
    return <PaceDisabledEmptyState />;
  }

  return (
    <div
      className="grid h-[calc(100vh-4rem)] gap-4 md:grid-cols-[280px_1fr]"
      data-testid="pace-page"
    >
      {/* Left rail */}
      <aside className="flex min-h-0 flex-col overflow-hidden rounded-2xl border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border p-3">
          <div className="flex items-center gap-2">
            <img src="/brand/pace/pace-avatar-64.png" alt="" className="h-6 w-6 rounded-full ring-1 ring-secondary/40" />
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">Pace</div>
          </div>
          <Button size="sm" variant="outline" onClick={newConversation} data-testid="pace-new-conversation">
            <Plus className="mr-1 h-3.5 w-3.5" /> New
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {loadingList && <div className="p-4 text-xs text-muted-foreground">Loading…</div>}
          {!loadingList && conversations.length === 0 && (
            <div className="p-4 text-xs text-muted-foreground">
              Start a new conversation to see it here.
            </div>
          )}
          {conversations.map((c) => (
            <div
              key={c.id}
              className={
                "group mb-1 flex items-center gap-1 rounded-lg px-2 py-2 text-sm transition-colors " +
                (c.id === activeId
                  ? "bg-accent text-primary"
                  : "text-muted-foreground hover:bg-accent/60 hover:text-primary")
              }
              data-testid={`pace-conv-${c.id}`}
            >
              <button
                type="button"
                onClick={() => openConversation(c.id)}
                className="min-w-0 flex-1 text-left"
                data-testid={`pace-conv-open-${c.id}`}
              >
                <div className="truncate font-medium text-primary">{c.title}</div>
                <div className="text-[11px] text-muted-foreground">
                  {c.message_count} msgs · {relative(c.last_message_at)}
                </div>
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    aria-label="Options"
                    className="rounded-md p-1 opacity-0 transition-opacity hover:bg-accent group-hover:opacity-100 focus:opacity-100"
                    data-testid={`pace-conv-menu-${c.id}`}
                  >
                    <MoreVertical className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onSelect={() => {
                      setRenameFor(c);
                      setRenameText(c.title || "");
                    }}
                  >
                    Rename
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-danger focus:bg-danger/10 focus:text-danger"
                    onSelect={() => setConfirmDelete(c)}
                  >
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ))}
        </div>
      </aside>

      {/* Right pane */}
      <main className="flex min-h-0 flex-col overflow-hidden rounded-2xl border border-border bg-card">
        <div className="flex items-center gap-3 border-b border-border px-5 py-3">
          <MessageSquare className="h-4 w-4 text-secondary" />
          <div className="min-w-0 flex-1">
            <div className="truncate font-display text-base font-semibold text-primary" data-testid="pace-thread-title">
              {thread?.conversation?.title || "New conversation"}
            </div>
          </div>
        </div>

        <div
          ref={scrollerRef}
          className="flex-1 space-y-4 overflow-y-auto px-5 py-4"
          data-testid="pace-thread"
        >
          {!activeId && !sending && (
            <EmptyState onPick={(q) => send(q)} />
          )}
          {loadingThread && <div className="text-xs text-muted-foreground">Loading…</div>}
          {thread?.messages?.map((m) => (
            <MessageBubble key={m.id} message={m} />
          ))}
          {sending && streamingText && (
            <MessageBubble
              key="streaming"
              message={{
                id: "streaming",
                role: "assistant",
                content: streamingText,
                rendered_by: "llm",
              }}
              streaming
            />
          )}
          {sending && !streamingText && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="pace-typing-indicator">
              <TypingDots />
              <span>Pace is thinking…</span>
            </div>
          )}
        </div>

        <div className="border-t border-border p-3">
          <div className="flex items-end gap-2">
            <Textarea
              ref={composerRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onComposerKey}
              placeholder="Ask Pace about your time… (Shift+Enter for newline)"
              rows={2}
              className="max-h-40 min-h-[3.5rem] resize-y"
              maxLength={2000}
              data-testid="pace-composer"
              disabled={sending}
            />
            <Button
              onClick={() => send()}
              disabled={sending || !draft.trim()}
              data-testid="pace-send"
              className="shrink-0"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              <span className="ml-1.5">Send</span>
            </Button>
          </div>
        </div>
      </main>

      {/* Rename dialog */}
      <AlertDialog open={!!renameFor} onOpenChange={(o) => !o && setRenameFor(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Rename conversation</AlertDialogTitle>
            <AlertDialogDescription>
              Titles must be 1-60 characters.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            value={renameText}
            onChange={(e) => setRenameText(e.target.value.slice(0, 60))}
            maxLength={60}
            data-testid="pace-rename-input"
          />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={rename} data-testid="pace-rename-confirm">
              Save
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirm */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this conversation?</AlertDialogTitle>
            <AlertDialogDescription>
              This hides it from your history. It won't be shown again unless a {brand} operator restores it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={remove}
              className="bg-danger hover:bg-danger/90"
              data-testid="pace-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function EmptyState({ onPick }) {
  return (
    <div className="mx-auto flex max-w-md flex-col items-center py-10 text-center" data-testid="pace-empty-state">
      <img src="/brand/pace/pace-avatar-256.png" alt="Pace" className="mb-4 h-24 w-24 rounded-full ring-2 ring-secondary/30" />
      <h2 className="font-display text-xl font-semibold text-primary">Ask Pace anything about your time</h2>
      <p className="mt-2 text-sm text-muted-foreground">
        Numbers come from what Pace can see for you — hours, entries, PTO, and policy.
      </p>
      <div className="mt-6 space-y-2">
        {STARTERS.map((s, i) => (
          <button
            key={i}
            type="button"
            onClick={() => onPick(s)}
            data-testid={`pace-starter-${i}`}
            className="w-full rounded-full border border-border bg-background px-4 py-2 text-sm text-primary transition-colors hover:border-secondary/40 hover:bg-accent"
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

function MessageBubble({ message, streaming = false }) {
  const isUser = message.role === "user";
  const tone =
    message.rendered_by === "fallback"
      ? "template"
      : message.rendered_by === "refusal"
        ? "scope"
        : message.rendered_by === "llm"
          ? "ai"
          : null;
  return (
    <div
      data-testid={streaming ? "pace-msg-streaming" : `pace-msg-${message.role}`}
      className={"flex gap-3 " + (isUser ? "justify-end" : "justify-start")}
    >
      {!isUser && (
        <img
          src="/brand/pace/pace-avatar-64.png"
          alt=""
          className="h-8 w-8 shrink-0 rounded-full ring-1 ring-secondary/40"
        />
      )}
      <div className={"max-w-[75%] rounded-2xl px-4 py-3 text-sm leading-relaxed " +
          (isUser
            ? "bg-primary text-primary-foreground"
            : "bg-accent/40 text-primary")
        }>
        <div className="whitespace-pre-wrap">
          {message.content}
          {streaming && <span className="ml-0.5 inline-block h-4 w-1.5 animate-pulse bg-secondary align-middle" aria-hidden />}
        </div>
        {!isUser && tone && !streaming && (
          <div className="mt-2 text-[10px] uppercase tracking-widest text-muted-foreground/70">
            {tone}
          </div>
        )}
      </div>
    </div>
  );
}

function TypingDots() {
  return (
    <span className="inline-flex items-center gap-1" aria-label="typing">
      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-secondary [animation-delay:-0.32s]" />
      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-secondary [animation-delay:-0.16s]" />
      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-secondary" />
    </span>
  );
}

/**
 * Phase 27c.2 — Pace-disabled empty state.
 *
 * Renders when `/api/pace/status` returns `pace_enabled: false`.
 * Regular tenant users see a clear "ask your account admin" message.
 * Platform admins get an extra CTA that deep-links straight to the
 * master console for the current tenant so they can flip the toggle
 * without hunting through the tenants list.
 */
function PaceDisabledEmptyState() {
  const { user, isPlatformAdmin } = useAuth();
  const tenantId = user?.tenant_id;
  return (
    <div
      className="mx-auto max-w-2xl py-16 text-center"
      data-testid="pace-not-enabled"
    >
      <Sparkles className="mx-auto mb-4 h-10 w-10 text-secondary" />
      <h1 className="font-display text-2xl font-semibold text-primary">
        Pace AI isn't enabled for this workspace yet.
      </h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Contact your account admin to turn it on.
      </p>
      {isPlatformAdmin && tenantId && (
        <div className="mt-6" data-testid="pace-not-enabled-master-cta-wrap">
          <Link
            to={`/master/tenants/${tenantId}`}
            className="inline-flex items-center gap-2 rounded-full border border-secondary/40 bg-secondary/10 px-4 py-2 text-sm font-medium text-primary transition-colors hover:bg-secondary/20"
            data-testid="pace-not-enabled-master-cta"
          >
            <Sparkles className="h-4 w-4 text-secondary" />
            Enable Pace for this workspace →
          </Link>
        </div>
      )}
    </div>
  );
}
